import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, jsonBody } from "../api/client";
import type { WorkspaceRole } from "../api/types";
import { BrandLockup } from "../brand/BrandMark";
import { formatMoney, titleCase } from "../lib/format";
import { ErrorPanel, Field, FormActions, Notice } from "../ui/components";
import { BoundarySkeleton } from "../ui/skeletons";
import { authorityLabel } from "./permissions";

interface CommercialOffer {
  planCode: string;
  planName: string;
  billingInterval: "monthly" | "annual";
  currency: string;
  priceMinor: number;
  introductoryTrialDays: number;
}

interface InvitationPreview {
  tenantSlug: string;
  tenantName: string;
  eventRole: WorkspaceRole;
  bookingRole: WorkspaceRole;
  requiresPassword: boolean;
  commercialOffer?: CommercialOffer;
}
interface Acceptance {
  tenantSlug: string;
  tenantName: string;
  requiresLogin: boolean;
  commercial: boolean;
}

export function EmployeeInvitationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = new URLSearchParams(location.hash.slice(1)).get("token") ?? "";
  const preview = useQuery({
    queryKey: ["employee-invitation", token],
    queryFn: () =>
      api<InvitationPreview>("/api/v1/invitations/preview", {
        method: "POST",
        body: jsonBody({ token }),
      }),
    enabled: token.length >= 32,
    retry: false,
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [accepted, setAccepted] = useState<Acceptance | null>(null);

  async function accept(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setWorking(true);
    setError(undefined);
    try {
      setAccepted(
        await api<Acceptance>("/api/v1/invitations/accept", {
          method: "POST",
          body: jsonBody({ token, password: String(form.get("password")) }),
        }),
      );
    } catch (caught) {
      setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (!token)
    return (
      <main className="auth-panel">
        <div className="auth-card">
          <Notice tone="danger">This employee invitation link is incomplete.</Notice>
        </div>
      </main>
    );
  if (preview.isLoading) return <BoundarySkeleton />;
  if (preview.error)
    return (
      <main className="auth-panel">
        <div className="auth-card">
          <ErrorPanel error={preview.error} />
        </div>
      </main>
    );
  // Both halves are named so somebody invited to only one side is told which side that is, rather
  // than being shown a single role that no longer describes what they hold.
  const invitedAs = authorityLabel(
    preview.data?.eventRole ?? "none",
    preview.data?.bookingRole ?? "none",
  );
  return (
    <main className="auth-layout">
      <section className="auth-brand">
        <BrandLockup />
        <div>
          <h1>
            {preview.data?.commercialOffer ? "Set up your workspace." : "Join the venue team."}
          </h1>
          <p>
            {preview.data?.commercialOffer
              ? "Your approved package stays bound to this invitation through secure Stripe setup."
              : "Your fixed role and venue scope are enforced by the server on every request."}
          </p>
        </div>
        <footer>Single-use invitation · Passwords are never emailed or logged.</footer>
      </section>
      <section className="auth-panel">
        <div className="auth-card">
          <h1>{preview.data?.tenantName}</h1>
          <p>You are invited as {invitedAs}.</p>
          {preview.data?.commercialOffer ? (
            <Notice tone="info" title="Invitation-bound package">
              <dl className="definition-list">
                <dt>Package</dt>
                <dd>{preview.data.commercialOffer.planName}</dd>
                <dt>Billing interval</dt>
                <dd>{titleCase(preview.data.commercialOffer.billingInterval)}</dd>
                <dt>Price</dt>
                <dd>
                  {formatMoney(
                    preview.data.commercialOffer.priceMinor,
                    preview.data.commercialOffer.currency,
                  )}{" "}
                  / {preview.data.commercialOffer.billingInterval === "annual" ? "year" : "month"}
                </dd>
                <dt>Introductory offer</dt>
                <dd>
                  {preview.data.commercialOffer.introductoryTrialDays
                    ? `${preview.data.commercialOffer.introductoryTrialDays} days before the first charge`
                    : "Standard billing starts at checkout"}
                </dd>
              </dl>
            </Notice>
          ) : null}
          {error ? <ErrorPanel error={error} /> : null}
          {accepted ? (
            <div className="stack">
              <Notice tone="success">Access accepted for {accepted.tenantName}.</Notice>
              <button
                className="button primary"
                type="button"
                onClick={() =>
                  navigate("/login", {
                    replace: true,
                    state: { from: accepted.commercial ? "/onboarding" : "/" },
                  })
                }
              >
                {accepted.requiresLogin ? "Sign in" : "Continue"}
              </button>
            </div>
          ) : (
            <form className="form-stack" onSubmit={accept}>
              {preview.data?.requiresPassword ? (
                <Field
                  label="Create password"
                  hint="Use at least 12 characters and a password manager."
                >
                  <input
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    required
                  />
                </Field>
              ) : null}
              <FormActions
                submitting={working}
                submitLabel={
                  preview.data?.commercialOffer
                    ? "Accept owner invitation"
                    : "Accept employee invitation"
                }
              />
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
